import { useState } from "react";
import { Facebook, Link2, Mail, MessageCircle, Twitter } from "lucide-react";
import { ARTICLES, articleBySlug } from "@/data/articles";
import { sectionById } from "@/data/sections";
import { Link, useDocumentTitle } from "@/lib/router";
import { stamp } from "@/lib/format";
import { CardStory, Img } from "@/components/Story";
import { EmiCalculator, Newsletter, Trending } from "@/components/Sidebar";
import { NotFound } from "./Listing";

function Body({ paragraphs }: { paragraphs: string[] }) {
  return (
    <div className="article-body">
      {paragraphs.map((p, i) => {
        if (p.startsWith("## ")) return <h2 key={i}>{p.slice(3)}</h2>;
        if (p.startsWith("> ")) return <blockquote key={i}>{p.slice(2)}</blockquote>;
        return <p key={i}>{p}</p>;
      })}
    </div>
  );
}

function Share({ title }: { title: string }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const enc = encodeURIComponent;
  const btn = "flex size-9 items-center justify-center rounded-full border border-ink hover:bg-ink hover:text-white";
  return (
    <div className="flex items-center gap-2">
      <a className={btn} aria-label="Share on WhatsApp" target="_blank" rel="noreferrer" href={`https://wa.me/?text=${enc(title + " " + url)}`}>
        <MessageCircle className="size-4" />
      </a>
      <a className={btn} aria-label="Share on X" target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`}>
        <Twitter className="size-4" />
      </a>
      <a className={btn} aria-label="Share on Facebook" target="_blank" rel="noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`}>
        <Facebook className="size-4" />
      </a>
      <a className={btn} aria-label="Share by email" href={`mailto:?subject=${enc(title)}&body=${enc(url)}`}>
        <Mail className="size-4" />
      </a>
      <button
        className={btn}
        aria-label="Copy link"
        onClick={() => {
          navigator.clipboard?.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        <Link2 className="size-4" />
      </button>
      {copied && <span className="text-sm font-bold text-brand">Copied!</span>}
    </div>
  );
}

export function ArticlePage({ slug }: { slug: string }) {
  const a = articleBySlug(slug);
  useDocumentTitle(a ? `${a.title} | BrickBharat` : "Page not found | BrickBharat");
  if (!a) return <NotFound />;

  const section = sectionById(a.section)!;
  const related = ARTICLES.filter(x => x.slug !== a.slug && (x.section === a.section || (a.city && x.city === a.city))).slice(0, 3);

  return (
    <main className="mx-auto max-w-[1240px] px-4 pt-6">
      <div className="grid gap-10 lg:grid-cols-12">
        <article className="lg:col-span-8">
          <nav className="flex gap-2 text-[13px] font-extrabold uppercase tracking-wide" aria-label="Breadcrumb">
            <Link href={`/section/${section.id}`} className="text-brand hover:underline">
              {section.label}
            </Link>
            {a.city && (
              <>
                <span className="text-muted">/</span>
                <Link href={`/search?q=${encodeURIComponent(a.city)}`} className="hover:text-brand">
                  {a.city}
                </Link>
              </>
            )}
          </nav>
          {a.exclusive && (
            <span className="mt-3 inline-block bg-brand px-2 py-0.5 text-[12px] font-extrabold uppercase tracking-wider text-white">
              Exclusive
            </span>
          )}
          <h1 className="headline mt-2 text-[2.1rem] sm:text-[2.9rem]">{a.title}</h1>
          <p className="mt-3 font-[family-name:var(--font-body)] text-xl leading-snug text-ink-soft">{a.dek}</p>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-4 border-y border-rule py-3">
            <div>
              <p className="text-[14px] font-extrabold uppercase">By {a.author}</p>
              <p className="text-[13px] text-muted">Published {stamp(a.hoursAgo)}</p>
            </div>
            <Share title={a.title} />
          </div>

          <figure className="mt-5">
            <Img src={a.image} alt={a.imageCaption ?? a.title} className="aspect-[16/9]" video={a.video} />
            {a.imageCaption && <figcaption className="mt-2 text-[13px] text-muted">{a.imageCaption}</figcaption>}
          </figure>

          <div className="mt-6">
            <Body paragraphs={a.body} />
          </div>

          <div className="mt-6 flex flex-wrap gap-2 border-t border-rule pt-4">
            {a.tags.map(t => (
              <Link key={t} href={`/search?q=${encodeURIComponent(t)}`} className="border border-ink px-2.5 py-1 text-[12px] font-extrabold uppercase tracking-wide hover:bg-ink hover:text-white">
                {t}
              </Link>
            ))}
          </div>

          <div className="mt-8">
            <Newsletter dark />
          </div>

          {related.length > 0 && (
            <section className="mt-10">
              <h2 className="splash mb-4 border-t-[6px] border-brand pt-2 text-[1.8rem]">Read Next</h2>
              <div className="grid gap-6 sm:grid-cols-3">
                {related.map(r => (
                  <CardStory key={r.slug} a={r} size="sm" />
                ))}
              </div>
            </section>
          )}
        </article>

        <aside className="space-y-8 lg:col-span-4">
          <div className="space-y-8 lg:sticky lg:top-16">
            <Trending />
            <EmiCalculator />
          </div>
        </aside>
      </div>
    </main>
  );
}
